import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import api from "../api/axios";
import "../assets/styles/newsletters.css";

const PAGE_NAME = "Email Newsletters";
const headers = (action) => ({ "x-page-name": PAGE_NAME, "x-action": action });
const requestError = (error, fallback) => {
  if (error.response?.status !== 403) toast.error(error.response?.data?.message || fallback);
};
const emptyTemplate = () => ({ name: "", subject: "", preheader: "", status: "draft", content_blocks: [] });
const emptyCampaign = () => ({ name: "", template_id: "", subject: "", preheader: "", scheduled_at: "", track_opens: false, track_clicks: false, compliance_confirmed: false });
const newBlock = (type) => {
  if (type === "heading") return { type, text: "Your headline", align: "center" };
  if (type === "text") return { type, text: "Write your message here.", align: "left" };
  if (type === "image") return { type, image_url: "", link_url: "", alt: "", width: 640, align: "center" };
  if (type === "button") return { type, label: "Shop now", url: "", style: "primary", align: "center" };
  return { type: "divider", align: "center" };
};
const fmt = (value) => value ? new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";
const percent = (value, total) => total ? `${((Number(value || 0) / total) * 100).toFixed(1)}%` : "0%";

const Newsletters = () => {
  const [tab, setTab] = useState("campaigns");
  const [overview, setOverview] = useState({ subscribed: 0, unsubscribed: 0, templates: 0, campaigns: 0 });
  const [permissions, setPermissions] = useState({ can_view: true, can_add: false, can_edit: false, can_delete: false });
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [templateForm, setTemplateForm] = useState(emptyTemplate);
  const [campaignForm, setCampaignForm] = useState(emptyCampaign);
  const [report, setReport] = useState(null);
  const [uploadingBlock, setUploadingBlock] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testTemplate, setTestTemplate] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const config = { headers: headers("view") };
      const [overviewResponse, templateResponse, campaignResponse, subscriberResponse] = await Promise.all([
        api.get("/admin/newsletters/overview", config),
        api.get("/admin/newsletters/templates", config),
        api.get("/admin/newsletters/campaigns", config),
        api.get("/admin/newsletters/subscribers?limit=100", config),
      ]);
      setOverview(overviewResponse.data?.data || {});
      setPermissions(overviewResponse.data?.data?.permissions || { can_view: true, can_add: false, can_edit: false, can_delete: false });
      setTemplates(templateResponse.data?.data || []);
      setCampaigns(campaignResponse.data?.data || []);
      setSubscribers(subscriberResponse.data?.data || []);
    } catch (error) {
      requestError(error, "Unable to load newsletter management.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const activeTemplates = useMemo(() => templates.filter((item) => item.status !== "archived"), [templates]);

  const openTemplate = (template = null) => {
    setEditingTemplateId(template?.template_id || null);
    setTemplateForm(template ? {
      name: template.name || "",
      subject: template.subject || "",
      preheader: template.preheader || "",
      status: template.status || "draft",
      content_blocks: Array.isArray(template.content_blocks) ? template.content_blocks.map((block) => ({ ...block })) : [],
    } : emptyTemplate());
    setTemplateOpen(true);
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    if (!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.content_blocks.length) {
      toast.error("Name, subject, and at least one content block are required.");
      return;
    }
    try {
      setSaving(true);
      const action = editingTemplateId ? "edit" : "add";
      const response = editingTemplateId
        ? await api.put(`/admin/newsletters/templates/${editingTemplateId}`, templateForm, { headers: headers(action) })
        : await api.post("/admin/newsletters/templates", templateForm, { headers: headers(action) });
      toast.success(response.data?.message || "Template saved.");
      setTemplateOpen(false);
      await loadAll();
    } catch (error) {
      requestError(error, "Unable to save the email template.");
    } finally { setSaving(false); }
  };

  const updateBlock = (index, field, value) => setTemplateForm((previous) => ({
    ...previous,
    content_blocks: previous.content_blocks.map((block, blockIndex) => blockIndex === index ? { ...block, [field]: value } : block),
  }));
  const moveBlock = (index, direction) => setTemplateForm((previous) => {
    const target = index + direction;
    if (target < 0 || target >= previous.content_blocks.length) return previous;
    const blocks = [...previous.content_blocks];
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    return { ...previous, content_blocks: blocks };
  });
  const removeBlock = (index) => setTemplateForm((previous) => ({ ...previous, content_blocks: previous.content_blocks.filter((_, i) => i !== index) }));

  const uploadImage = async (index, file) => {
    if (!file) return;
    const payload = new FormData();
    payload.append("image", file);
    try {
      setUploadingBlock(index);
      const response = await api.post("/admin/newsletters/assets", payload, {
        headers: headers(editingTemplateId ? "edit" : "add"),
      });
      updateBlock(index, "image_url", response.data?.data?.image_url || "");
      toast.success("Image uploaded.");
    } catch (error) {
      requestError(error, "Unable to upload the email image.");
    } finally { setUploadingBlock(null); }
  };

  const openTestDialog = (template) => {
    setTestTemplate(template);
    setTestEmail("");
    setTestDialogOpen(true);
  };

  const closeTestDialog = () => {
    if (testSending) return;
    setTestDialogOpen(false);
    setTestTemplate(null);
    setTestEmail("");
  };

  const sendTest = async (event) => {
    event.preventDefault();
    const email = testEmail.trim().toLowerCase();
    if (!testTemplate || !email) {
      toast.error("Enter an email address for the test.");
      return;
    }
    try {
      setTestSending(true);
      const response = await api.post(`/admin/newsletters/templates/${testTemplate.template_id}/test`, { email }, { headers: headers("edit") });
      toast.success(response.data?.message || "Test sent.");
      setTestDialogOpen(false);
      setTestTemplate(null);
      setTestEmail("");
    } catch (error) { requestError(error, "Unable to send the test email."); }
    finally { setTestSending(false); }
  };

  const deleteTemplate = (template) => confirmDialog({
    header: "Delete email template?",
    message: `Delete “${template.name}”? Templates used by campaigns must be archived instead.`,
    icon: "pi pi-exclamation-triangle",
    acceptClassName: "p-button-danger",
    accept: async () => {
      try {
        await api.delete(`/admin/newsletters/templates/${template.template_id}`, { headers: headers("delete") });
        toast.success("Template deleted.");
        await loadAll();
      } catch (error) { requestError(error, "Unable to delete the template."); }
    },
  });

  const openCampaign = (campaign = null) => {
    setEditingCampaignId(campaign?.campaign_id || null);
    setCampaignForm(campaign ? {
      name: campaign.name || "",
      template_id: String(campaign.template_id || ""),
      subject: campaign.subject || "",
      preheader: campaign.preheader || "",
      scheduled_at: campaign.scheduled_at ? new Date(new Date(campaign.scheduled_at).getTime() - new Date(campaign.scheduled_at).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "",
      track_opens: Boolean(campaign.track_opens),
      track_clicks: Boolean(campaign.track_clicks),
      compliance_confirmed: false,
    } : emptyCampaign());
    setCampaignOpen(true);
  };

  const selectCampaignTemplate = (id) => {
    const selected = templates.find((item) => String(item.template_id) === String(id));
    setCampaignForm((previous) => ({ ...previous, template_id: id, subject: selected?.subject || previous.subject, preheader: selected?.preheader || previous.preheader }));
  };

  const saveCampaign = async (event, schedule = false) => {
    event.preventDefault();
    if (!campaignForm.name.trim() || !campaignForm.template_id || !campaignForm.subject.trim()) {
      toast.error("Campaign name, template, and subject are required."); return;
    }
    if (schedule && !campaignForm.scheduled_at) { toast.error("Choose a send date and time."); return; }
    if (schedule && !campaignForm.compliance_confirmed) { toast.error("Confirm the campaign compliance checklist."); return; }
    try {
      setSaving(true);
      let id = editingCampaignId;
      const payload = { ...campaignForm, template_id: Number(campaignForm.template_id) };
      if (id) await api.put(`/admin/newsletters/campaigns/${id}`, payload, { headers: headers("edit") });
      else {
        const response = await api.post("/admin/newsletters/campaigns", payload, { headers: headers("add") });
        id = response.data?.data?.campaign_id;
      }
      if (schedule) {
        await api.post(`/admin/newsletters/campaigns/${id}/schedule`, {
          scheduled_at: new Date(campaignForm.scheduled_at).toISOString(),
          compliance_confirmed: true,
        }, { headers: headers("edit") });
      }
      toast.success(schedule ? "Campaign scheduled." : "Campaign draft saved.");
      setCampaignOpen(false);
      await loadAll();
    } catch (error) { requestError(error, "Unable to save the campaign."); }
    finally { setSaving(false); }
  };

  const sendNow = (campaign) => confirmDialog({
    header: "Send newsletter now?",
    message: `Send “${campaign.name}” to all currently subscribed, active customers? This cannot be undone.`,
    icon: "pi pi-send",
    accept: async () => {
      try {
        const response = await api.post(`/admin/newsletters/campaigns/${campaign.campaign_id}/send`, { compliance_confirmed: true }, { headers: headers("edit") });
        toast.success(response.data?.message || "Campaign queued.");
        await loadAll();
      } catch (error) { requestError(error, "Unable to queue the campaign."); }
    },
  });

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/admin/newsletters/campaigns/${campaign.campaign_id}/cancel`, {}, { headers: headers("edit") });
      toast.success("Campaign cancelled."); await loadAll();
    } catch (error) { requestError(error, "Unable to cancel the campaign."); }
  };

  const openReport = async (campaign) => {
    try {
      const response = await api.get(`/admin/newsletters/campaigns/${campaign.campaign_id}/report?limit=100`, { headers: headers("view") });
      setReport(response.data?.data || null); setReportOpen(true);
    } catch (error) { requestError(error, "Unable to load the campaign report."); }
  };

  const suppress = (subscriber) => confirmDialog({
    header: "Suppress this address?",
    message: `${subscriber.email} will not receive future newsletters, even if a campaign is already queued.`,
    icon: "pi pi-ban",
    acceptClassName: "p-button-danger",
    accept: async () => {
      try {
        await api.post(`/admin/newsletters/subscribers/${subscriber.subscriber_id}/suppress`, {}, { headers: headers("edit") });
        toast.success("Address suppressed."); await loadAll();
      } catch (error) { requestError(error, "Unable to suppress the address."); }
    },
  });

  return (
    <section className="admin-page newsletter-page">
      <ConfirmDialog />
      <div className="page-header newsletter-heading">
        <div><p>Customer engagement</p><h1>Email Newsletters</h1><span>Build consent-based campaigns, send promotions, and review engagement.</span></div>
        <div className="newsletter-header-actions">
          {permissions.can_add && <button type="button" onClick={() => { setTab("templates"); openTemplate(); }}><i className="pi pi-file-plus" /> New template</button>}
          {permissions.can_add && <button type="button" className="primary-action" onClick={() => { setTab("campaigns"); openCampaign(); }} disabled={!activeTemplates.length}><i className="pi pi-send" /> New campaign</button>}
        </div>
      </div>

      <div className="newsletter-stats">
        <div><i className="pi pi-users" /><span><strong>{overview.subscribed || 0}</strong>Subscribed customers</span></div>
        <div><i className="pi pi-ban" /><span><strong>{overview.unsubscribed || 0}</strong>Unsubscribed / suppressed</span></div>
        <div><i className="pi pi-palette" /><span><strong>{overview.templates || 0}</strong>Available templates</span></div>
        <div><i className="pi pi-chart-bar" /><span><strong>{overview.campaigns || 0}</strong>Total campaigns</span></div>
      </div>

      <div className="newsletter-tabs" role="tablist">
        {[['campaigns','Campaigns','pi-send'],['templates','Templates','pi-palette'],['subscribers','Subscribers','pi-users']].map(([value,label,icon]) => (
          <button key={value} className={tab === value ? "active" : ""} onClick={() => setTab(value)}><i className={`pi ${icon}`} /> {label}</button>
        ))}
      </div>

      <div className="page-card newsletter-workspace">
        {loading ? <div className="newsletter-empty"><i className="pi pi-spin pi-spinner" /> Loading newsletters...</div> : tab === "campaigns" ? (
          campaigns.length ? <div className="newsletter-campaign-list">{campaigns.map((campaign) => (
            <article key={campaign.campaign_id} className="newsletter-campaign-card">
              <div className="newsletter-campaign-main"><span className={`newsletter-status ${campaign.status}`}>{campaign.status}</span><div><h2>{campaign.name}</h2><p>{campaign.subject}</p><small>Template: {campaign.template?.name || "Unavailable"} · {campaign.status === "scheduled" ? `Scheduled ${fmt(campaign.scheduled_at)}` : `Created ${fmt(campaign.createdAt)}`}</small></div></div>
              <div className="newsletter-campaign-metrics"><span><strong>{campaign.sent_count || 0}</strong>SMTP accepted</span><span><strong>{campaign.failed_count || 0}</strong>Failed</span><span><strong>{percent(campaign.unique_open_count, campaign.sent_count)}</strong>Unique opens*</span><span><strong>{percent(campaign.unique_click_count, campaign.sent_count)}</strong>Unique clicks</span></div>
              <div className="newsletter-card-actions">
                <button onClick={() => openReport(campaign)}><i className="pi pi-chart-line" /> Report</button>
                {permissions.can_edit && ['draft','scheduled'].includes(campaign.status) && <button onClick={() => openCampaign(campaign)}><i className="pi pi-pencil" /> Edit</button>}
                {permissions.can_edit && ['draft','scheduled','failed'].includes(campaign.status) && <button className="primary" onClick={() => sendNow(campaign)}><i className="pi pi-send" /> Send now</button>}
                {permissions.can_edit && ['scheduled','sending'].includes(campaign.status) && <button className="danger" onClick={() => cancelCampaign(campaign)}><i className="pi pi-times" /> Cancel</button>}
              </div>
            </article>
          ))}</div> : <div className="newsletter-empty"><i className="pi pi-send" /><h2>No campaigns yet</h2><p>Create a template first, then prepare your first newsletter campaign.</p></div>
        ) : tab === "templates" ? (
          templates.length ? <div className="newsletter-template-grid">{templates.map((template) => (
            <article key={template.template_id} className="newsletter-template-card"><div className="newsletter-template-preview"><i className="pi pi-envelope" /><span>{template.content_blocks?.length || 0} blocks</span></div><div><span className={`newsletter-status ${template.status}`}>{template.status}</span><h2>{template.name}</h2><p>{template.subject}</p><div className="newsletter-card-actions">{permissions.can_edit && <button onClick={() => openTemplate(template)}><i className="pi pi-pencil" /> Edit</button>}{permissions.can_edit && <button onClick={() => openTestDialog(template)}><i className="pi pi-envelope" /> Test</button>}{permissions.can_delete && <button className="danger" onClick={() => deleteTemplate(template)}><i className="pi pi-trash" /></button>}</div></div></article>
          ))}</div> : <div className="newsletter-empty"><i className="pi pi-palette" /><h2>No email templates</h2><p>Build a reusable email from text, image, button, and divider blocks.</p></div>
        ) : (
          <div className="newsletter-subscriber-table"><div className="newsletter-compliance-note"><i className="pi pi-shield" /><p><strong>Consent-only audience.</strong> Registration does not imply consent. This list contains customers who actively selected the newsletter opt-in; unsubscribed and suppressed addresses remain recorded so they cannot be mailed accidentally.</p></div><table><thead><tr><th>Customer</th><th>Status</th><th>Consent source</th><th>Consent date</th><th></th></tr></thead><tbody>{subscribers.map((subscriber) => <tr key={subscriber.subscriber_id}><td><strong>{subscriber.name || "Customer"}</strong><small>{subscriber.email}</small></td><td><span className={`newsletter-status ${subscriber.status}`}>{subscriber.status}</span></td><td>{subscriber.consent_source.replaceAll("_", " ")}</td><td>{fmt(subscriber.consent_at)}</td><td>{permissions.can_edit && subscriber.status === "subscribed" && <button className="icon-danger" onClick={() => suppress(subscriber)} title="Suppress address"><i className="pi pi-ban" /></button>}</td></tr>)}</tbody></table>{!subscribers.length && <div className="newsletter-empty"><p>No customers have opted in yet.</p></div>}</div>
        )}
      </div>

      <Dialog
        visible={testDialogOpen}
        modal
        className="newsletter-test-dialog"
        header="Send test email"
        onHide={closeTestDialog}
        closable={!testSending}
        draggable={false}
      >
        <form className="newsletter-test-form" onSubmit={sendTest}>
          <div className="newsletter-test-intro">
            <i className="pi pi-envelope" aria-hidden="true" />
            <div>
              <strong>{testTemplate?.name || "Email template"}</strong>
              <span>A test message will be sent through the configured SMTP account.</span>
            </div>
          </div>

          <label htmlFor="newsletter-test-email">
            Recipient email address
          </label>
          <input
            id="newsletter-test-email"
            type="email"
            value={testEmail}
            onChange={(event) => setTestEmail(event.target.value)}
            placeholder="name@example.com"
            autoComplete="email"
            autoFocus
            required
            disabled={testSending}
          />

          <div className="newsletter-test-actions">
            <button type="button" onClick={closeTestDialog} disabled={testSending}>
              Cancel
            </button>
            <button type="submit" className="primary-action" disabled={testSending || !testEmail.trim()}>
              <i className={`pi ${testSending ? "pi-spin pi-spinner" : "pi-send"}`} />
              {testSending ? "Sending..." : "Send test"}
            </button>
          </div>
        </form>
      </Dialog>

      <Sidebar visible={templateOpen} position="right" className="newsletter-builder-sidebar" header={editingTemplateId ? "Edit email template" : "Create email template"} onHide={() => !saving && setTemplateOpen(false)}>
        <form className="newsletter-builder" onSubmit={saveTemplate}>
          <div className="newsletter-builder-top-actions">
            <span>
              {templateForm.content_blocks.length} content {templateForm.content_blocks.length === 1 ? "block" : "blocks"}
            </span>
            <div>
              <button type="button" onClick={() => setTemplateOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="primary-action" disabled={saving}>
                <i className={`pi ${saving ? "pi-spin pi-spinner" : "pi-save"}`} />
                {saving ? "Saving..." : "Save template"}
              </button>
            </div>
          </div>
          <div className="newsletter-form-grid"><label><span>Template name *</span><input value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} maxLength="150" /></label><label><span>Status</span><select value={templateForm.status} onChange={(e) => setTemplateForm({ ...templateForm, status: e.target.value })}><option value="draft">Draft</option><option value="active">Active</option><option value="archived">Archived</option></select></label></div>
          <label><span>Email subject * <small>Supports {`{{first_name}}`}</small></span><input value={templateForm.subject} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} maxLength="255" /></label>
          <label><span>Inbox preview text</span><input value={templateForm.preheader} onChange={(e) => setTemplateForm({ ...templateForm, preheader: e.target.value })} maxLength="255" /></label>
          <div className="newsletter-add-block"><span>Add content</span>{['heading','text','image','button','divider'].map((type) => <button type="button" key={type} onClick={() => setTemplateForm((previous) => ({ ...previous, content_blocks: [...previous.content_blocks, newBlock(type)] }))}><i className={`pi ${type === 'image' ? 'pi-image' : type === 'button' ? 'pi-link' : type === 'divider' ? 'pi-minus' : 'pi-align-left'}`} /> {type}</button>)}</div>
          <div className="newsletter-blocks">{templateForm.content_blocks.map((block, index) => <div className="newsletter-block-editor" key={`${block.type}-${index}`}><div className="newsletter-block-toolbar"><strong>{index + 1}. {block.type}</strong><span><button type="button" onClick={() => moveBlock(index, -1)} disabled={!index}><i className="pi pi-arrow-up" /></button><button type="button" onClick={() => moveBlock(index, 1)} disabled={index === templateForm.content_blocks.length - 1}><i className="pi pi-arrow-down" /></button><button type="button" className="danger" onClick={() => removeBlock(index)}><i className="pi pi-trash" /></button></span></div>
            {['heading','text'].includes(block.type) && (block.type === 'text' ? <textarea rows="4" value={block.text} onChange={(e) => updateBlock(index, 'text', e.target.value)} /> : <input value={block.text} onChange={(e) => updateBlock(index, 'text', e.target.value)} />)}
            {block.type === 'image' && <><div className="newsletter-image-upload">{block.image_url ? <img src={block.image_url} alt="Email block preview" /> : <i className="pi pi-image" />}<label className="upload-button"><i className={`pi ${uploadingBlock === index ? 'pi-spin pi-spinner' : 'pi-upload'}`} /> {uploadingBlock === index ? 'Uploading...' : 'Upload image'}<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => uploadImage(index, e.target.files?.[0])} disabled={uploadingBlock !== null} /></label></div><label><span>Image destination URL</span><input type="url" value={block.link_url} onChange={(e) => updateBlock(index, 'link_url', e.target.value)} placeholder="https://..." /></label><label><span>Alternative text</span><input value={block.alt} onChange={(e) => updateBlock(index, 'alt', e.target.value)} /></label></>}
            {block.type === 'button' && <div className="newsletter-form-grid"><label><span>Button label</span><input value={block.label} onChange={(e) => updateBlock(index, 'label', e.target.value)} /></label><label><span>Destination URL</span><input type="url" value={block.url} onChange={(e) => updateBlock(index, 'url', e.target.value)} placeholder="https://..." /></label><label><span>Style</span><select value={block.style} onChange={(e) => updateBlock(index, 'style', e.target.value)}><option value="primary">Primary</option><option value="secondary">Secondary</option></select></label></div>}
            {block.type !== 'divider' && <label className="newsletter-align"><span>Alignment</span><select value={block.align || 'left'} onChange={(e) => updateBlock(index, 'align', e.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>}
          </div>)}</div>
          <div className="newsletter-live-preview"><h3>Email preview</h3><div className="newsletter-preview-canvas">{templateForm.content_blocks.map((block,index) => block.type === 'heading' ? <h2 key={index} style={{textAlign:block.align}}>{block.text}</h2> : block.type === 'text' ? <p key={index} style={{textAlign:block.align}}>{block.text}</p> : block.type === 'image' ? (block.image_url ? <img key={index} src={block.image_url} alt={block.alt || ''} /> : null) : block.type === 'button' ? <div key={index} style={{textAlign:block.align}}><span className={`preview-button ${block.style}`}>{block.label}</span></div> : <hr key={index} />)}<footer>Business address and unsubscribe link are inserted automatically.</footer></div></div>
          <div className="newsletter-form-actions"><button type="button" onClick={() => setTemplateOpen(false)}>Cancel</button><button type="submit" className="primary-action" disabled={saving}>{saving ? "Saving..." : "Save template"}</button></div>
        </form>
      </Sidebar>

      <Sidebar visible={campaignOpen} position="right" className="newsletter-campaign-sidebar" header={editingCampaignId ? "Edit newsletter campaign" : "Create newsletter campaign"} onHide={() => !saving && setCampaignOpen(false)}>
        <form className="newsletter-builder" onSubmit={(e) => saveCampaign(e, false)}>
          <label><span>Internal campaign name *</span><input value={campaignForm.name} onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })} /></label>
          <label><span>Email template *</span><select value={campaignForm.template_id} onChange={(e) => selectCampaignTemplate(e.target.value)}><option value="">Select template</option>{activeTemplates.map((item) => <option value={item.template_id} key={item.template_id}>{item.name}</option>)}</select></label>
          <label><span>Subject *</span><input value={campaignForm.subject} onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })} /></label>
          <label><span>Preview text</span><input value={campaignForm.preheader} onChange={(e) => setCampaignForm({ ...campaignForm, preheader: e.target.value })} /></label>
          <label><span>Schedule send (local time)</span><input type="datetime-local" value={campaignForm.scheduled_at} onChange={(e) => setCampaignForm({ ...campaignForm, scheduled_at: e.target.value })} /></label>
          <div className="newsletter-tracking-options">
            <label><input type="checkbox" checked={campaignForm.track_clicks} onChange={(e) => setCampaignForm({ ...campaignForm, track_clicks: e.target.checked })} /><span>Track link clicks</span></label>
            <label><input type="checkbox" checked={campaignForm.track_opens} onChange={(e) => setCampaignForm({ ...campaignForm, track_opens: e.target.checked })} /><span>Track opens with a pixel</span></label>
            <small>Tracking is off by default. Enable it only when your privacy notice and applicable consent or lawful basis cover email analytics.</small>
          </div>
          <div className="newsletter-send-warning"><i className="pi pi-info-circle" /><p>Only subscribed active customers are included. Eligibility is checked again immediately before delivery.</p></div>
          <label className="newsletter-compliance-check"><input type="checkbox" checked={campaignForm.compliance_confirmed} onChange={(e) => setCampaignForm({ ...campaignForm, compliance_confirmed: e.target.checked })} /><span>I confirm the sender and subject are accurate, this message is promotional, all links and claims are truthful, and the campaign is appropriate for this consented audience.</span></label>
          <div className="newsletter-form-actions"><button type="submit" disabled={saving}>Save draft</button><button type="button" className="primary-action" disabled={saving || !campaignForm.scheduled_at || !campaignForm.compliance_confirmed} onClick={(e) => saveCampaign(e, true)}>Schedule</button></div>
        </form>
      </Sidebar>

      <Sidebar visible={reportOpen} position="right" className="newsletter-report-sidebar" header="Campaign report" onHide={() => setReportOpen(false)}>{report && <div className="newsletter-report"><h2>{report.campaign.name}</h2><p>{report.campaign.subject}</p><div className="newsletter-report-grid"><span><strong>{report.campaign.recipient_count}</strong>Recipients</span><span><strong>{report.campaign.sent_count}</strong>SMTP accepted</span><span><strong>{report.campaign.failed_count}</strong>Failed</span><span><strong>{report.campaign.unique_open_count}</strong>Unique opens*</span><span><strong>{report.campaign.unique_click_count}</strong>Unique clicks</span><span><strong>{report.campaign.unsubscribe_count}</strong>Unsubscribed</span></div><p className="newsletter-report-note">* Open tracking is approximate because mail clients may block or preload images. “SMTP accepted” is not proof of inbox delivery; provider webhooks are required for delivered, bounced, and complaint states.</p><h3>Recipients</h3><div className="newsletter-report-recipients">{report.recipients.map((recipient) => <div key={recipient.recipient_id}><span><strong>{recipient.name || 'Customer'}</strong><small>{recipient.email}</small></span><span className={`newsletter-status ${recipient.status}`}>{recipient.status}</span></div>)}</div></div>}</Sidebar>
    </section>
  );
};

export default Newsletters;
